import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

/**
 * Generic CRUD page.
 * props: title, subtitle, base (api path), perm (e.g. "MODULE"), columns, fields,
 *        idKey="id", parseList (optional), defaults (optional object)
 */
export default function ResourcePage({ title, subtitle, base, perm, columns, fields, idKey = "id", parseList, defaults = {} }) {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(base);
      const d = res.data.data;
      const list = parseList ? parseList(d) : (Array.isArray(d) ? d : d.items || []);
      setRows(list);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [base, parseList]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...defaults }); setOpen(true); };
  const openEdit = (row) => {
    const f = {};
    fields.forEach((fl) => { f[fl.name] = row[fl.name] ?? (fl.type === "tags" ? [] : ""); });
    setEditing(row); setForm(f); setOpen(true);
  };

  const setField = (name, val) => setForm((p) => ({ ...p, [name]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((fl) => {
        let v = form[fl.name];
        if (fl.type === "number") v = v === "" || v == null ? null : Number(v);
        if (fl.type === "tags" && typeof v === "string") v = v.split(",").map((s) => s.trim()).filter(Boolean);
        payload[fl.name] = v;
      });
      if (editing) {
        await api.put(`${base}/${editing[idKey]}`, payload);
        toast.success(`${title} updated`);
      } else {
        await api.post(base, payload);
        toast.success(`${title} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${title.replace(/s$/, "")}?`)) return;
    try {
      await api.delete(`${base}/${row[idKey]}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    }
  };

  const canCreate = hasPermission(`${perm}.CREATE`);
  const canEdit = hasPermission(`${perm}.EDIT`);

  const allColumns = [
    ...columns,
    {
      key: "_actions", header: "",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          {canEdit && (
            <button onClick={() => openEdit(row)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`edit-${row[idKey]}`}>
              <Pencil className="h-4 w-4" />
            </button>
          )}
          {canEdit && (
            <button onClick={() => remove(row)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`delete-${row[idKey]}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid={`resource-${perm.toLowerCase()}`}>
      <PageHeader
        title={title} subtitle={subtitle}
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="resource-create-btn">
            <Plus className="h-4 w-4" /> New {title.replace(/s$/, "")}
          </button>
        )}
      />
      <DataTable columns={allColumns} rows={rows} loading={loading} testid={`${perm.toLowerCase()}-table`} />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="resource-dialog">
            <h3 className="text-lg font-bold text-foreground mb-4">{editing ? "Edit" : "Create"} {title.replace(/s$/, "")}</h3>
            <div className="space-y-3.5">
              {fields.map((fl) => (
                <div key={fl.name}>
                  <label className="block text-sm font-medium text-foreground mb-1">{fl.label}</label>
                  {fl.type === "select" ? (
                    <select value={form[fl.name] ?? ""} onChange={(e) => setField(fl.name, e.target.value)}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40" data-testid={`field-${fl.name}`}>
                      <option value="">Select…</option>
                      {(fl.options || []).map((o) => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
                    </select>
                  ) : fl.type === "boolean" ? (
                    <select value={String(form[fl.name] ?? false)} onChange={(e) => setField(fl.name, e.target.value === "true")}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`field-${fl.name}`}>
                      <option value="false">No</option><option value="true">Yes</option>
                    </select>
                  ) : (
                    <input
                      type={fl.type === "number" ? "number" : "text"}
                      value={fl.type === "tags" && Array.isArray(form[fl.name]) ? form[fl.name].join(", ") : (form[fl.name] ?? "")}
                      onChange={(e) => setField(fl.name, e.target.value)}
                      disabled={editing && fl.immutable}
                      placeholder={fl.placeholder}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/40 disabled:bg-muted"
                      data-testid={`field-${fl.name}`}
                    />
                  )}
                  {fl.hint && <p className="text-xs text-muted-foreground mt-1">{fl.hint}</p>}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted" data-testid="dialog-cancel">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="dialog-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
